import React from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Bell,
  Building2,
  Check,
  ChevronDown,
  Clock,
  FolderGit2,
  Loader2,
  Radio,
  ShieldAlert,
  Target,
  Wand2,
  X,
} from 'lucide-react';
import {
  AgentCapabilityRequestedPayloadSchema,
  widenedMatcherFor,
  type AgentCapabilityAnswer,
  type AgentCapabilityRequestedPayload,
  type AgentCapabilityResolvedPayload,
} from '@reflex/shared';
import { cn } from '@reflex/ui/lib/utils';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { Button } from '@reflex/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { StreamCardBubble } from '@reflex/ui/components/stream/StreamCardBubble';
import { decideCapabilityRequest } from '../../api.js';
import { CapabilityDockContext } from './CapabilityDockContext.js';
import type { AgentLifecycleItem } from './types';

/**
 * The card an agent's capability request renders in its thread. It says
 * three things and no more: what the agent wants (headline, with a breadth
 * pill because "watch PRs" can mean one repo or the whole organization),
 * one line of the facts that decide it (stream, filter or params, volume,
 * lease), and the agent's own reason. The full facts sit behind Details.
 *
 * Two buttons answer it: Allow (for this agent) and Deny. Each carries a
 * menu for the wider answers, once or all your agents, and for the widen
 * option, the one deliberate way to grant a step wider than the exact
 * matcher the agent asked with.
 *
 * While pending, and when the host provides a dock (SDKChatView does), the
 * card lifts out of the transcript into the slot above the composer, so it
 * is in reach however far the thread has scrolled. A later
 * `agent.capability_resolved` for the same request folds it back into the
 * thread as a resolved line (the host passes `resolution`), so a request
 * answered from the dock, another tab, or Slack reads the same here.
 */

const ANSWER_LABEL: Record<AgentCapabilityAnswer, string> = {
  allow_once: 'Allowed once',
  allow_agent: 'Allowed for this agent',
  allow_always: 'Always allowed',
  deny: 'Denied',
  never: 'Never allowed',
};

const BREADTH: Record<
  NonNullable<AgentCapabilityRequestedPayload['breadth']>,
  { label: string; Icon: typeof Target; className: string }
> = {
  object: {
    label: 'One object',
    Icon: Target,
    className: 'border-status-info/40 bg-status-info/10 text-status-info',
  },
  scope: {
    label: 'One container',
    Icon: FolderGit2,
    className: 'border-status-warning/40 bg-status-warning/10 text-status-warning',
  },
  org: {
    label: 'Whole organization',
    Icon: Building2,
    className: 'border-status-error/40 bg-status-error/10 text-status-error',
  },
};

function kindOf(payload: AgentCapabilityRequestedPayload): {
  label: string;
  Icon: typeof Bell;
} {
  if (payload.requestKind === 'action') return { label: 'Action', Icon: Wand2 };
  switch (payload.request.kind) {
    case 'listen.watch':
      return { label: 'Watch', Icon: Radio };
    case 'listen.timer':
      return { label: 'Timer', Icon: Clock };
    default:
      return { label: 'Listener', Icon: Bell };
  }
}

function formatLease(ms: number): string {
  const hours = Math.round(ms / 3_600_000);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`;
}

/** `key: json` lines from an action card become a two-column table. */
function parseParamLines(lines: readonly string[]): Array<[string, string]> {
  return lines.map((line) => {
    const idx = line.indexOf(': ');
    if (idx < 0) return [line, ''];
    const key = line.slice(0, idx);
    const raw = line.slice(idx + 2);
    try {
      const value = JSON.parse(raw) as unknown;
      return [key, typeof value === 'string' ? value : JSON.stringify(value)];
    } catch {
      return [key, raw];
    }
  });
}

export function parseCapabilityRequest(
  item: AgentLifecycleItem,
): AgentCapabilityRequestedPayload | null {
  const parsed = AgentCapabilityRequestedPayloadSchema.safeParse(item.sourceEvent?.payload);
  return parsed.success ? parsed.data : null;
}

export function CapabilityRequestCard({
  item,
  agentId,
  resolution,
  canDecide = true,
}: {
  item: AgentLifecycleItem;
  agentId: string;
  /** The answer already given, when a later event resolved this request. */
  resolution?: AgentCapabilityResolvedPayload | null;
  /** False on read-only surfaces (replays, viewers without `agents:run`). */
  canDecide?: boolean;
}) {
  const dock = React.useContext(CapabilityDockContext);
  const payload = React.useMemo(() => parseCapabilityRequest(item), [item]);
  const [local, setLocal] = React.useState<{
    answer: AgentCapabilityAnswer;
    decision: 'allow' | 'deny';
  } | null>(null);
  const [widen, setWiden] = React.useState(false);
  const [pending, setPending] = React.useState<AgentCapabilityAnswer | null>(null);
  const [expanded, setExpanded] = React.useState(false);

  if (!payload) return null;
  const { label: kindLabel, Icon: KindIcon } = kindOf(payload);
  const isAction = payload.requestKind === 'action';
  const breadth = payload.breadth ? BREADTH[payload.breadth] : null;
  const wider = widenedMatcherFor(payload.request);
  const resolved = resolution
    ? { answer: resolution.answer, decision: resolution.decision }
    : local;
  const params = isAction ? parseParamLines(payload.filterLines) : [];
  const destructive = isAction && /\(destructive\)/.test(payload.headline);
  const severe = destructive || payload.breadth === 'org';
  const isDeciding = pending !== null;

  const decide = async (answer: AgentCapabilityAnswer) => {
    if (resolved || pending) return;
    setPending(answer);
    try {
      await decideCapabilityRequest(agentId, payload.requestId, answer, widen);
      setLocal({ answer, decision: answer === 'deny' || answer === 'never' ? 'deny' : 'allow' });
    } catch (err) {
      toast.error('Could not record the decision', { description: toErrorMessage(err) });
    } finally {
      setPending(null);
    }
  };

  // Resolved: a compact line, with the details one click away.
  if (resolved) {
    const allowed = resolved.decision === 'allow';
    return (
      <div
        data-event-id={item.sourceEvent?.id}
        data-testid="capability-request-card-resolved"
        className="overflow-hidden rounded-xl border border-border/60 bg-card"
      >
        <div className="flex items-center gap-2.5 px-3 py-2 text-xs">
          <span
            className={cn(
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
              allowed
                ? 'bg-status-success/15 text-status-success'
                : 'bg-status-error/15 text-status-error',
            )}
          >
            {allowed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
          </span>
          <span
            className={cn(
              'shrink-0 font-medium',
              allowed ? 'text-status-success' : 'text-status-error',
            )}
          >
            {ANSWER_LABEL[resolved.answer]}
          </span>
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{payload.headline}</span>
          <DetailsToggle expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
        </div>
        {expanded && (
          <div className="space-y-2 border-t border-border/40 bg-muted/20 px-3 py-3">
            <Facts payload={payload} params={params} />
            <Reason payload={payload} />
          </div>
        )}
      </div>
    );
  }

  const card = (
    <div data-event-id={item.sourceEvent?.id}>
      <StreamCardBubble
        icon={<KindIcon className="h-4 w-4" />}
        color={severe ? 'red' : 'amber'}
        timestamp={item.timestamp}
        event={item.sourceEvent}
        data-testid="capability-request-card"
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p
            className={cn(
              'text-[10px] font-semibold uppercase tracking-wider',
              severe ? 'text-status-error/90' : 'text-status-warning/90',
            )}
          >
            Needs your approval · {kindLabel}
          </p>
          {breadth && (
            <span
              data-testid="capability-breadth"
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-2xs font-semibold',
                breadth.className,
              )}
            >
              <breadth.Icon className="h-2.5 w-2.5" />
              {breadth.label}
            </span>
          )}
          {destructive && (
            <span className="inline-flex items-center gap-1 rounded-full border border-status-error/40 bg-status-error/10 px-1.5 py-px text-2xs font-semibold text-status-error">
              <ShieldAlert className="h-2.5 w-2.5" />
              destructive
            </span>
          )}
        </div>
        <p
          className="mt-0.5 text-[15px] font-semibold leading-snug text-foreground"
          data-testid="capability-headline"
        >
          {payload.headline}
        </p>
        <Summary payload={payload} params={params} />
        <Reason payload={payload} className="mt-1 line-clamp-1" />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canDecide && (
            <>
              <SplitAnswer
                label="Allow for this agent"
                answer="allow_agent"
                variant="default"
                pending={pending}
                disabled={isDeciding}
                onDecide={decide}
                menuTestId="capability-allow-more"
                widen={wider ? { label: wider.label, checked: widen, onChange: setWiden } : null}
                widenTestId="capability-widen"
              >
                <DropdownMenuItem
                  disabled={isDeciding}
                  onSelect={() => void decide('allow_once')}
                  data-testid="capability-answer-allow_once"
                >
                  Allow once
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={isDeciding}
                  onSelect={() => void decide('allow_always')}
                  data-testid="capability-answer-allow_always"
                >
                  Allow for all my agents
                </DropdownMenuItem>
              </SplitAnswer>
              <SplitAnswer
                label="Deny"
                answer="deny"
                variant="outline"
                className="border-status-error/30 text-status-error hover:bg-status-error/10 hover:text-status-error"
                pending={pending}
                disabled={isDeciding}
                onDecide={decide}
                menuTestId="capability-deny-more"
                widen={wider ? { label: wider.label, checked: widen, onChange: setWiden } : null}
                widenTestId="capability-widen-deny"
              >
                <DropdownMenuItem
                  disabled={isDeciding}
                  onSelect={() => void decide('never')}
                  className="text-status-error focus:text-status-error"
                  data-testid="capability-answer-never"
                >
                  Never, for any of my agents
                </DropdownMenuItem>
              </SplitAnswer>
            </>
          )}
          <span className="ml-auto">
            <DetailsToggle expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
          </span>
        </div>
        {expanded && (
          <div className="mt-2 rounded-lg border border-border/40 bg-background/40 px-3 py-2.5">
            <Facts payload={payload} params={params} />
          </div>
        )}
      </StreamCardBubble>
    </div>
  );

  // Pending and answerable: lift into the dock above the composer. Render
  // nothing inline until the slot mounts (avoids a one-frame inline flash).
  // Read-only surfaces keep the card in the thread where it was asked.
  if (dock.enabled && canDecide) {
    return dock.dockEl ? createPortal(card, dock.dockEl) : null;
  }
  return card;
}

function DetailsToggle({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {expanded ? 'Hide' : 'Details'}
      <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
    </button>
  );
}

/**
 * A primary answer with a menu of its wider siblings. The widen checkbox
 * lives in both menus because it changes what "this agent", "all my
 * agents", and "never" write; the two checkboxes share one state.
 */
function SplitAnswer({
  label,
  answer,
  variant,
  className,
  pending,
  disabled,
  onDecide,
  menuTestId,
  widen,
  widenTestId,
  children,
}: {
  label: string;
  answer: AgentCapabilityAnswer;
  variant: 'default' | 'outline';
  className?: string;
  pending: AgentCapabilityAnswer | null;
  disabled: boolean;
  onDecide: (answer: AgentCapabilityAnswer) => void;
  menuTestId: string;
  widen: { label: string; checked: boolean; onChange: (v: boolean) => void } | null;
  widenTestId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="inline-flex">
      <Button
        type="button"
        size="sm"
        variant={variant}
        className={cn('h-7 rounded-r-none text-xs', className)}
        disabled={disabled}
        onClick={() => onDecide(answer)}
        data-testid={`capability-answer-${answer}`}
      >
        {pending === answer ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        {label}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant={variant}
            className={cn('h-7 rounded-l-none border-l-0 px-1.5', className)}
            disabled={disabled}
            aria-label={`More ${label.toLowerCase()} options`}
            data-testid={menuTestId}
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-56 text-xs">
          {children}
          {widen && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={widen.checked}
                onCheckedChange={widen.onChange}
                onSelect={(e) => e.preventDefault()}
                data-testid={widenTestId}
              >
                <span>
                  <span className="text-muted-foreground">Also cover </span>
                  {widen.label}
                </span>
              </DropdownMenuCheckboxItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function Reason({
  payload,
  className,
}: {
  payload: AgentCapabilityRequestedPayload;
  className?: string;
}) {
  return (
    <p
      className={cn('text-xs italic text-muted-foreground', className)}
      data-testid="capability-reason"
      title={payload.reason}
    >
      “{payload.reason}”
    </p>
  );
}

function interruptsOf(payload: AgentCapabilityRequestedPayload): boolean {
  return payload.interrupts || payload.alsoRequests.some((r) => r.kind === 'listen.interrupt');
}

/** The facts that decide the answer, on one line, separated by dots. */
function Summary({
  payload,
  params,
}: {
  payload: AgentCapabilityRequestedPayload;
  params: Array<[string, string]>;
}) {
  const isAction = payload.requestKind === 'action';
  const isWatch = payload.request.kind === 'listen.watch';
  const busy = payload.sourceEventsLastWeek !== null && payload.sourceEventsLastWeek > 50;
  const parts: React.ReactNode[] = [];
  if (payload.stream) parts.push(<span key="stream">{payload.stream}</span>);
  if (!isAction && payload.filterLines.length > 0) {
    parts.push(
      <span key="filter" className="text-foreground" data-testid="capability-filter">
        {payload.filterLines.join(', ')}
      </span>,
    );
  }
  if (!isAction && isWatch && payload.filterLines.length === 0) {
    parts.push(
      <span
        key="unfiltered"
        className="inline-flex items-center gap-1 font-medium text-status-error"
        data-testid="capability-unfiltered"
      >
        <AlertTriangle className="h-3 w-3" />
        No filter: every event of this kind in the organization
      </span>,
    );
  }
  if (isAction && params.length > 0) {
    parts.push(
      <span key="params" className="font-mono text-2xs" data-testid="capability-params">
        {params.map(([k, v], i) => (
          <span key={k}>
            {i > 0 && <span className="text-muted-foreground/60"> · </span>}
            <span className="text-muted-foreground">{k}</span>{' '}
            <span className="text-foreground">{v}</span>
          </span>
        ))}
      </span>,
    );
  }
  if (payload.sourceEventsLastWeek !== null) {
    parts.push(
      <span key="volume" className={cn(busy && 'font-medium text-status-warning')}>
        up to {payload.sourceEventsLastWeek} events/week
      </span>,
    );
  }
  if (payload.leaseMs !== null)
    parts.push(<span key="lease">for {formatLease(payload.leaseMs)}</span>);
  if (interruptsOf(payload)) {
    parts.push(
      <span key="interrupt" className="font-medium text-status-warning">
        may interrupt a running turn
      </span>,
    );
  }
  if (parts.length === 0) return null;
  return (
    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mx-1.5 text-muted-foreground/50">·</span>}
          {part}
        </React.Fragment>
      ))}
    </p>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="pt-px text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="min-w-0 text-xs text-foreground">{children}</dd>
    </>
  );
}

function Pill({ children, tone }: { children: React.ReactNode; tone?: 'warn' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-2xs font-medium',
        tone === 'warn'
          ? 'border-status-warning/40 bg-status-warning/10 text-status-warning'
          : 'border-border/60 bg-muted/60 text-foreground',
      )}
    >
      {children}
    </span>
  );
}

/** The full facts grid, behind Details. */
function Facts({
  payload,
  params,
}: {
  payload: AgentCapabilityRequestedPayload;
  params: Array<[string, string]>;
}) {
  const isAction = payload.requestKind === 'action';
  const isWatch = payload.request.kind === 'listen.watch';
  const alsoInterrupts = interruptsOf(payload);
  const hasCadence = payload.leaseMs !== null || payload.maxFires !== null || alsoInterrupts;

  return (
    <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3 gap-y-2">
      {payload.stream && <Fact label="Listens to">{payload.stream}</Fact>}
      {!isAction && payload.filterLines.length > 0 && (
        <Fact label="Only when">
          <ul className="flex flex-wrap gap-1">
            {payload.filterLines.map((line) => (
              <li key={line}>
                <Pill>{line}</Pill>
              </li>
            ))}
          </ul>
        </Fact>
      )}
      {!isAction && isWatch && payload.filterLines.length === 0 && (
        <Fact label="Only when">
          <span className="font-medium text-status-error">
            No filter: every event of this kind in the organization.
          </span>
        </Fact>
      )}
      {isAction && params.length > 0 && (
        <Fact label="With">
          <table className="w-full border-separate border-spacing-0">
            <tbody>
              {params.map(([key, value]) => (
                <tr key={key}>
                  <td className="w-28 pr-2 align-top font-mono text-2xs text-muted-foreground">
                    {key}
                  </td>
                  <td className="break-all font-mono text-2xs text-foreground">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Fact>
      )}
      {payload.sourceEventsLastWeek !== null && (
        <Fact label="Volume">
          up to {payload.sourceEventsLastWeek} events from this source last week
        </Fact>
      )}
      {hasCadence && (
        <Fact label="Runs">
          <span className="flex flex-wrap gap-1">
            {payload.leaseMs !== null && <Pill>for {formatLease(payload.leaseMs)}</Pill>}
            {payload.maxFires !== null && (
              <Pill>{payload.maxFires > 1 ? 'every time' : 'once'}</Pill>
            )}
            {alsoInterrupts && <Pill tone="warn">may interrupt a running turn</Pill>}
          </span>
        </Fact>
      )}
    </dl>
  );
}
